import { t } from '../strings';
/**
 * The Quick-Create Modal wrapper (piece 5). One generic construction reused
 * across record types, not three separately built modals — the same reuse
 * discipline the Button, the Pill and the Item Table follow.
 *
 * It is the mechanism behind every "Add New X" row on a Dropdown; the
 * specification says of that row that clicking it opens the relevant creation
 * modal inline.
 */

/**
 * Which button opened the window: a Dropdown's Add New row or a page's + New.
 * 🔒 The window is titled with that button's words (decision 1451).
 */
export type QuickCreateOpener = 'addNew' | 'new';

/**
 * The Title Bar reads the words on the button that opened it (decision 1451):
 * "Add New {X}" from a Dropdown's Add New row, "New {X}" from a page's + New.
 * 💰 It read "Add Company" behind a button reading + New Company (UI audit of
 * 2026-09-30, rank 6).
 */
export function quickCreateTitle(recordLabel: string, opener: QuickCreateOpener = 'addNew'): string {
  return opener === 'new' ? `${t('quickCreate.new')} ${recordLabel}` : `${t('dropdown.addNew')} ${recordLabel}`;
}

export interface CreatedRecord {
  id: string;
  label: string;
}

/**
 * On save the wrapper closes and returns the new record to the Dropdown that
 * opened it, auto-selected in that field. The wrapper never writes the record
 * itself: it nests the record page's own field form and maps that page's Save
 * and Cancel to the modal's Primary and Cancel.
 */
export interface QuickCreateOutcome {
  fieldId: string;
  selected: CreatedRecord;
  closed: true;
}

export function quickCreateOutcome(fieldId: string, created: CreatedRecord): QuickCreateOutcome {
  return { fieldId, selected: created, closed: true };
}

/** The two controls a nested record form hands the wrapper, mapped onto the Footer. */
export interface NestedFormActions {
  save: () => void;
  cancel: () => void;
}

export function footerFromNestedForm(actions: NestedFormActions): { primary: () => void; cancel: () => void } {
  return { primary: actions.save, cancel: actions.cancel };
}
